import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Title from './Title';
import Paper from '@mui/material/Paper';
import axios from "axios";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useNavigate } from "react-router-dom";
import LinearProgress from '@mui/material/LinearProgress';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function preventDefault(event) {
    event.preventDefault();
}

// Function to convert billing plan to readable format
function formatBillingPlan(billingPlan) {
    if (!billingPlan) return '';

    const mapping = {
        '1/1': 'M/M',
        '12/1': 'R/M',
        '12/12': 'R/R'
    };

    return mapping[billingPlan] || billingPlan;
}

// Function to format date as dd-MM-yyyy HH:mm
function formatDateTime(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}`;
}

// Function to format date as dd-MM-yyyy
function formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
}

// Function to check if year is leap year
function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

// Function to calculate subscription start date: TerminationDate - 1 year + 1 day
function calculateSubscriptionStartDate(terminationDate) {
    const termDate = new Date(terminationDate);

    // Go back 1 year
    const startDate = new Date(termDate);
    startDate.setFullYear(termDate.getFullYear() - 1);

    // Add 1 day (because inclusive)
    startDate.setDate(startDate.getDate() + 1);

    return startDate;
}

// Function to calculate days between two dates (inclusive - includes both start and end date)
function calculateDaysBetween(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive
    return diffDays;
}

// Function to calculate days in subscription year (accounting for leap years)
function calculateSubscriptionYearDays(terminationDate) {
    const termDate = new Date(terminationDate);
    const startDate = calculateSubscriptionStartDate(terminationDate);

    // Check if the period includes Feb 29
    const startYear = startDate.getFullYear();
    const endYear = termDate.getFullYear();

    // If it's a leap year and the period includes February 29, it's 366 days, otherwise 365
    if (startYear === endYear) {
        // Same year subscription
        return isLeapYear(startYear) ? 366 : 365;
    } else {
        // Subscription spans two years - check if Feb 29 is in the range
        let hasLeapDay = false;

        // Check if leap day exists in start year and is within range
        if (isLeapYear(startYear)) {
            const leapDay = new Date(startYear, 1, 29); // Feb 29
            if (leapDay >= startDate && leapDay <= termDate) {
                hasLeapDay = true;
            }
        }

        // Check if leap day exists in end year and is within range
        if (!hasLeapDay && isLeapYear(endYear)) {
            const leapDay = new Date(endYear, 1, 29); // Feb 29
            if (leapDay >= startDate && leapDay <= termDate) {
                hasLeapDay = true;
            }
        }

        return hasLeapDay ? 366 : 365;
    }
}

function Row(props) {
    const { order } = props;
    const [open, setOpen] = React.useState(false);
    const originalQuantity = parseInt(order.Quantity);
    const [newQuantity, setNewQuantity] = React.useState(originalQuantity);
    const [added, setAdded] = React.useState(0);
    const [plannedLaunchDate, setPlannedLaunchDate] = React.useState(null);

    // Parse dates for validation
    const terminationDate = new Date(order.TerminationDate);
    const subscriptionStartDate = calculateSubscriptionStartDate(order.TerminationDate);
    const amount = parseFloat(order.Amount);

    // Calculate days (inclusive - both start and end dates are counted)
    const totalDays = calculateSubscriptionYearDays(order.TerminationDate);
    const daysFromPlannedToTermination = plannedLaunchDate
        ? calculateDaysBetween(plannedLaunchDate, terminationDate)
        : 0;

    // Calculate price per day
    const pricePerDay = totalDays > 0 ? amount / totalDays : 0;

    // Calculate cost: daysFromPlannedToTermination * pricePerDay * Added
    const cost = daysFromPlannedToTermination * pricePerDay * added;

    const handleNewQuantityChange = (value) => {
        // Allow empty string or any input during typing
        if (value === '') {
            setNewQuantity('');
            setAdded(0);
            return;
        }

        const qty = parseInt(value);
        if (isNaN(qty)) {
            return;
        }

        // Update without validation during typing
        setNewQuantity(qty);
        setAdded(Math.max(0, qty - originalQuantity));
    };

    const handleNewQuantityBlur = () => {
        // Apply validation only on blur
        if (newQuantity === '' || newQuantity < originalQuantity) {
            setNewQuantity(originalQuantity);
            setAdded(0);
        }
    };

    const handleAddedChange = (value) => {
        if (value === '') {
            setAdded('');
            setNewQuantity(originalQuantity);
            return;
        }

        const addedQty = parseInt(value);
        if (isNaN(addedQty)) {
            return;
        }

        const validAdded = Math.max(0, addedQty);
        setAdded(validAdded);
        setNewQuantity(originalQuantity + validAdded);
    };

    const handleAddedBlur = () => {
        // Apply validation only on blur
        if (added === '' || added < 0) {
            setAdded(0);
            setNewQuantity(originalQuantity);
        }
    };

    return (
        <React.Fragment>
            <TableRow>
                <TableCell sx={{ width: '60px', padding: '6px' }}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                        sx={{ padding: '4px' }}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{order.Id}</TableCell>
                <TableCell>
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: order.Status === 'active' ? '#4caf50' : '#f44336',
                            color: 'white',
                        }}
                    >
                        {order.Status}
                    </Box>
                </TableCell>
                <TableCell>{order.CustomerName}</TableCell>
                <TableCell>{order.ProductName}</TableCell>
                <TableCell>{order.ProductGroup}</TableCell>
                <TableCell align="center">{order.Quantity}</TableCell>
                <TableCell align="right">{order.Amount}</TableCell>
                <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatBillingPlan(order.BillingPlan)}
                    </Typography>
                </TableCell>
                <TableCell align="right">{formatDate(order.TerminationDate)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, backgroundColor: '#f5f5f5' }} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            {/* Details Table */}
                            <Table size="small" aria-label="details" sx={{ mb: 3 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>ParentId</strong></TableCell>
                                        <TableCell><strong>CustomerId</strong></TableCell>
                                        <TableCell><strong>ProductId</strong></TableCell>
                                        <TableCell><strong>BillingPlanId</strong></TableCell>
                                        <TableCell><strong>CreatedAt</strong></TableCell>
                                        <TableCell><strong>DBCreatedAt</strong></TableCell>
                                        <TableCell><strong>DBUpdatedAt</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row">{order.ParentId}</TableCell>
                                        <TableCell>{order.CustomerId}</TableCell>
                                        <TableCell>{order.ProductId}</TableCell>
                                        <TableCell>{order.BillingPlanId}</TableCell>
                                        <TableCell>{formatDateTime(order.CreatedAt)}</TableCell>
                                        <TableCell>{formatDateTime(order.DBCreatedAt)}</TableCell>
                                        <TableCell>{formatDateTime(order.DBUpdatedAt)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {/* Simulation Section */}
                            <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, mb: 2 }}>
                                Symulacja rozszerzenia subskrypcji
                            </Typography>

                            {/* Quantity adjustment fields */}
                            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <TextField
                                    label="New Quantity"
                                    type="number"
                                    size="small"
                                    value={newQuantity}
                                    onChange={(e) => handleNewQuantityChange(e.target.value)}
                                    onBlur={handleNewQuantityBlur}
                                    inputProps={{ min: originalQuantity }}
                                    sx={{
                                        width: '150px',
                                        '& .MuiInputBase-input': {
                                            padding: '8.5px 14px',
                                            fontSize: '0.875rem'
                                        }
                                    }}
                                />
                                <TextField
                                    label="Added"
                                    type="number"
                                    size="small"
                                    value={added}
                                    onChange={(e) => handleAddedChange(e.target.value)}
                                    onBlur={handleAddedBlur}
                                    inputProps={{ min: 0 }}
                                    sx={{
                                        width: '150px',
                                        '& .MuiInputBase-input': {
                                            padding: '8.5px 14px',
                                            fontSize: '0.875rem'
                                        }
                                    }}
                                />
                                <Box>
                                    <DatePicker
                                        selected={plannedLaunchDate}
                                        onChange={(date) => setPlannedLaunchDate(date)}
                                        minDate={subscriptionStartDate}
                                        maxDate={terminationDate}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="Planned launch date"
                                        customInput={
                                            <TextField
                                                label="Planned launch date"
                                                size="small"
                                                sx={{
                                                    width: '200px',
                                                    '& .MuiInputBase-input': {
                                                        padding: '8.5px 14px',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        }
                                    />
                                </Box>
                            </Box>

                            {/* Calculations display */}
                            <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                    Obliczenia:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="body2">
                                        Okres subskrypcji: <strong>{formatDate(subscriptionStartDate)} → {formatDate(order.TerminationDate)}</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                        Dni w okresie subskrypcji (włącznie): <strong>{totalDays} dni</strong>
                                        {totalDays === 366 && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>(rok przestępny)</Typography>}
                                    </Typography>
                                    {plannedLaunchDate && (
                                        <Typography variant="body2">
                                            Dni od Planned launch date do TerminationDate (włącznie): <strong>{daysFromPlannedToTermination} dni</strong>
                                        </Typography>
                                    )}
                                    <Typography variant="body2">
                                        Cena za dzień: <strong>{pricePerDay.toFixed(4)} EUR</strong>
                                    </Typography>
                                    {plannedLaunchDate && added > 0 && (
                                        <Typography variant="body2" sx={{
                                            mt: 1,
                                            pt: 1,
                                            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                            fontSize: '0.95rem',
                                            fontWeight: 600,
                                            color: '#1976d2'
                                        }}>
                                            Cost: <strong>{cost.toFixed(2)} EUR</strong>
                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
                                                ({daysFromPlannedToTermination} dni × {pricePerDay.toFixed(4)} EUR × {added} licencji)
                                            </Typography>
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

export default function Subscriptions() {
    const [orders, setOrders] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedCustomer, setSelectedCustomer] = React.useState(null);

    React.useEffect(() => {
        (async () => {
            await axios
                .get("db/getsubscriptionsdh/")
                .then((response) => {
                    // Filter only R/R (12/12) subscriptions
                    const filteredOrders = response.data.filter(order => order.BillingPlan === '12/12');
                    setOrders(filteredOrders);
                })
                .catch((error) => {
                    console.log(error);
                    if (error?.response?.status === 401) {
                        alert("Your access is not allowed.");
                    }
                });
            setLoading(false);
        })();
    }, [setOrders]);

    // Get unique customer names for autocomplete
    const customerNames = React.useMemo(() => {
        const names = [...new Set(orders.map(order => order.CustomerName))].filter(Boolean);
        return names.sort();
    }, [orders]);

    // Filter orders by selected customer
    const filteredOrders = React.useMemo(() => {
        if (!selectedCustomer) {
            return orders;
        }
        return orders.filter(order => order.CustomerName === selectedCustomer);
    }, [orders, selectedCustomer]);

    const clearFilter = () => {
        setSelectedCustomer(null);
    };

    let navigate = useNavigate();
    const routeChange = () => {
        let path = "/orders";
        navigate(path);
    }

    const renderOrdersTable = (orders) => {
        return (
            <React.Fragment>
                {/* Filter Section */}
                <Paper sx={{ p: 2, width: '90%', margin: '20px auto 0' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Autocomplete
                            sx={{ minWidth: 300, flex: 1 }}
                            options={customerNames}
                            value={selectedCustomer}
                            onChange={(event, value) => setSelectedCustomer(value)}
                            renderInput={(params) => <TextField {...params} label="Filtruj po kliencie" variant="outlined" size="small" />}
                        />
                        <Button variant="outlined" onClick={clearFilter}>Wyczyść filtr</Button>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Znaleziono: <strong>{orders.length}</strong> subskrypcji
                        </Typography>
                    </Stack>
                </Paper>

                {/* Main Table */}
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '90%', margin: '20px auto 0' }}>
                    <Title>Subskrypcje</Title>
                    <TableContainer>
                        <Table size="small" aria-label="collapsible table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '60px', padding: '6px' }} />
                                    <TableCell>Id</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>CustomerName</TableCell>
                                    <TableCell>ProductName</TableCell>
                                    <TableCell>ProductGroup</TableCell>
                                    <TableCell align="center">Quantity</TableCell>
                                    <TableCell align="right">Cena BCM</TableCell>
                                    <TableCell align="center">Term/Invoice</TableCell>
                                    <TableCell align="right">TerminationDate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.map((order, index) => (
                                    <Row key={index} order={order} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </React.Fragment>
        );
    }

    let contents = loading
        ? (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
                <p><em>&nbsp;&nbsp;Loading... trwa ładowanie danych - stay calm:) ...</em></p>
            </Box>
        )
        : renderOrdersTable(filteredOrders);

    return (
        <>
            {contents}
        </>
    );
}
